import React from 'react';
import DataGrid, {
    Column,
    ColumnChooser,
    FilterRow,
    HeaderFilter,
    Paging,
    Lookup,
    RemoteOperations,
    Scrolling,
    Sorting,
} from 'devextreme-react/data-grid';
import type { AuditEntryDto } from '../../../dtos/AuditEntryDto';
import { AuditCategory } from '../../../dtos/AuditCategory';
import { Modules } from '../../../dtos/Modules';
import type { AuditLogControllerProps } from '../hooks/useAuditLogController';
import { CATEGORY_LABELS, MODULE_LABELS, actorLabel, roleLabel } from '../auditLabels';

// Lookups name the enum columns and give them a filter list. Unlike a computed cell value they
// leave the underlying value alone, so the grid still filters and sorts these columns in the
// database.
const moduleLookup = Object.entries(MODULE_LABELS)
    .map(([id, name]) => ({ id: Number(id) as Modules, name }));

const categoryLookup = Object.entries(CATEGORY_LABELS)
    .map(([id, name]) => ({ id: Number(id) as AuditCategory, name }));

// Who acted, with the role they held at that moment underneath. The role is the snapshot stored on
// the entry, not the user's role today.
const userCell = (cell: any) => {
    const entry = cell.data as AuditEntryDto;
    const role = roleLabel(entry.userRole);

    return (
        <span>
            {actorLabel(entry)}
            {role && <div style={{ fontSize: '0.85em', color: '#777' }}>{role}</div>}
        </span>
    );
};

const detailCell = (cell: any) => {
    const entry = cell.data as AuditEntryDto;

    if (entry.fieldName) {
        return (
            <span>
                {/* Details names the subject on rows whose subject is not a task or subtask -
                    an account edit, for one - so it is shown alongside the change. */}
                {entry.details && <span style={{ color: '#555' }}>{entry.details} — </span>}
                <strong>{entry.fieldName}</strong>{': '}
                <span style={{ textDecoration: 'line-through', color: '#a00' }}>
                    {entry.oldValue || <em>(empty)</em>}
                </span>
                {' → '}
                <span style={{ color: '#1a7f37' }}>{entry.newValue || <em>(empty)</em>}</span>
            </span>
        );
    }

    return <span>{entry.details}</span>;
};

const AuditLogView: React.FC<AuditLogControllerProps> = ({ store }) => (
    <div className="container-fluid" style={{ padding: '20px' }}>
        <h4>Audit Log</h4>
        <p style={{ color: '#666' }}>
            Every change, action and notification recorded across the application, newest first.
            Filter or sort any column to narrow it down.
        </p>

        <DataGrid
            dataSource={store}
            remoteOperations={true}
            showBorders
            columnAutoWidth
            wordWrapEnabled
            rowAlternationEnabled
            allowColumnResizing
            height="calc(100vh - 260px)"
        >
            <RemoteOperations filtering={true} paging={true} sorting={true} />
            {/* Virtual scrolling with paging off, matching the portfolio grid: rows load as you
                scroll rather than a page at a time. */}
            <Scrolling mode="virtual" rowRenderingMode="virtual" />
            <Paging enabled={false} />
            <Sorting mode="multiple" />
            {/* Header filters ask the server for a column's distinct values, which is a full scan
                on this table - so they are left on only for the small, indexed or lookup-backed
                columns. Everything else is narrowed with the filter row. */}
            <FilterRow visible={true} applyFilter="auto" />
            <HeaderFilter visible={true} />
            <ColumnChooser enabled={true} mode="select" />

            <Column
                dataField="timestamp"
                caption="Date / Time"
                dataType="datetime"
                format="MM/dd/yyyy h:mm:ss a"
                width={185}
                sortOrder="desc"
            />
            <Column dataField="fiscalYear" caption="Year" dataType="number" width={80} />
            <Column dataField="grantNumber" caption="Grant" width={155} allowHeaderFiltering={false} />
            <Column dataField="moduleId" caption="Module" width={155}>
                <Lookup dataSource={moduleLookup} valueExpr="id" displayExpr="name" />
            </Column>
            <Column dataField="phaseName" caption="Task" width={175} allowHeaderFiltering={false} />
            <Column dataField="stepName" caption="Subtask" width={175} allowHeaderFiltering={false} />
            <Column dataField="action" caption="Action" width={150} allowHeaderFiltering={false} />
            {/* Renders the field diff, not the details column it binds, so filtering here would
                miss the text on screen. Field / Old value / New value are filterable instead. */}
            <Column
                dataField="details"
                caption="Details"
                minWidth={280}
                cellRender={detailCell}
                allowSorting={false}
                allowFiltering={false}
                allowHeaderFiltering={false}
            />
            {/* A system row shows its job name, which lives in source rather than userName, so
                that column carries the filtering for background activity. */}
            <Column
                dataField="userName"
                caption="User"
                width={185}
                cellRender={userCell}
                allowHeaderFiltering={false}
            />
            <Column dataField="source" caption="System source" width={170} visible={false} />

            {/* Off by default - available from the column chooser when a search needs them. */}
            <Column dataField="category" caption="Area" width={140} visible={false}>
                <Lookup dataSource={categoryLookup} valueExpr="id" displayExpr="name" />
            </Column>
            <Column dataField="fieldName" caption="Field" width={150} visible={false} allowHeaderFiltering={false} />
            <Column dataField="oldValue" caption="Old value" width={180} visible={false} allowHeaderFiltering={false} />
            <Column dataField="newValue" caption="New value" width={180} visible={false} allowHeaderFiltering={false} />
        </DataGrid>
    </div>
);

export default AuditLogView;
